import React, { useEffect, useState } from 'react';
import classNames from "classnames";


const STYLE_PROPERTIES = {
    sm: {
        width: 21,
        height: 12,
        rectX: 6,
        viewBox: "0 0 21 12",
        circleRadius: 5.5,
        circleY: 5.9,
    },
    lg: {
        width: 30,
        height: 18,
        rectX: 9,
        viewBox: "0 0 30 18",
        circleRadius: 7.5,
        circleY: 8.9,
    },
} as const;

const TRANSFORM_FORMATTER = {
    lg: (enabled: boolean) => enabled ? "translate-x-[calc(100%-2px)]" : "translate-x-0.5",
    sm: (enabled: boolean) => enabled ? "translate-x-[calc(100%-2px)]" : "translate-x-px",
} as const;

interface SwitchInputProps {
    id?: string;
    size?: "sm" | "lg";
    value?: boolean;
    defaultValue?: boolean;
    disabled?: boolean;
    "aria-label"?: string;
    "aria-labelledby"?: string;
    "aria-expanded"?: boolean;
    "aria-controls"?: string;
    className?: string;
    onSwitch: (checked: boolean) => void;
}

const SwitchInput: React.FC<SwitchInputProps> = (props) => {
    const {
        id,
        size = "lg",
        value,
        defaultValue,
        disabled = false,
        "aria-label": ariaLabel,
        "aria-labelledby": ariaLabelledBy,
        "aria-expanded": ariaExpanded,
        "aria-controls": ariaControls,
        className: propClassName,
        onSwitch,
    } = props;
    const [enabled, setEnabled] = useState<boolean>();

    const { width, height, rectX, circleRadius, circleY, viewBox } = STYLE_PROPERTIES[size];

    const onChange = (checked: boolean) => {
        if (disabled) {
            return;
        }
        onSwitch(checked);
        setEnabled(checked);
    };

    useEffect(() => {
        if (value === undefined) {
            return;
        }
        setEnabled(value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    useEffect(() => {
        if (typeof value === "boolean") {
            return;
        }
        setEnabled(defaultValue ?? false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValue]);

    return (
        <label className={classNames(
                   "inline-flex",
                   size === "lg" ? "h-4.5 w-8" : "h-3 w-[21px]",
                   disabled ? "cursor-default" : "cursor-pointer",
                   propClassName
               )}
               onClick={e => e.stopPropagation()}
        >
            <input type="checkbox"
                   role="switch"
                   id={id}
                   className="sr-only peer"
                   checked={enabled ?? false}
                   disabled={disabled}
                   aria-label={ariaLabel}
                   aria-labelledby={ariaLabelledBy}
                   aria-expanded={ariaExpanded}
                   aria-controls={ariaControls}
                   onChange={e => onChange(e.target.checked)}
            />
            <svg aria-hidden="true"
                 width={width}
                 height={height}
                 viewBox={viewBox}
                 fill="none"
                 xmlns="http://www.w3.org/2000/svg"
            >
                <rect width={width} height={height} rx={rectX}
                      className={classNames(
                          "transition-colors",
                          (enabled && !disabled) ? "fill-chat42" : "fill-black-300"
                      )}
                />

                <circle cx={circleRadius}
                        cy={circleY}
                        r={circleRadius}
                        fill="white"
                        style={{ transformBox: "fill-box" }}
                        className={classNames(
                            "transition-transform",
                            TRANSFORM_FORMATTER[size](enabled ?? false),
                        )}
                />
            </svg>
        </label>
    );
};

export default SwitchInput;
